// Product Showcase 功能
class ProductShowcaseCheckout extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('.product-showcase-checkout-btn');
    this.loadingIcon = this.querySelector('.icon-load');
    this.buttonLabel = this.querySelector('.btn-label');
    this.originalText = this.buttonLabel?.textContent || 'Checkout';
    
    if (this.button) {
      this.button.addEventListener('click', this.handleCheckout.bind(this));
    }
  }
  
  async handleCheckout() {
    const productId = this.dataset.productId;
    const discountCode = this.dataset.discountCode;
    
    if (!productId) {
      console.error('Product ID not found');
      return;
    }
    
    // 显示加载状态
    this.setLoading(true);
    
    try {
      // 1. 首先清空购物车以确保只有当前产品
      await fetch('/cart/clear.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      // 2. 将产品添加到购物车
      const addResponse = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          id: productId,
          quantity: 1
        })
      });
      
      if (!addResponse.ok) {
        const errorData = await addResponse.json();
        throw new Error(errorData.description || '添加到购物车失败');
      }
      
      // 3. 如果有折扣码，先应用折扣码
      if (discountCode && discountCode.trim() !== '') {
        try {
          await fetch('/cart/update.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              attributes: {
                'discount_code': discountCode.trim()
              }
            })
          });
        } catch (discountError) {
          console.warn('应用折扣码失败，但继续结账:', discountError);
        }
      }
      
      // 4. 直接跳转到结账页面（带折扣码参数以防万一）
      let checkoutUrl = '/checkout';
      if (discountCode && discountCode.trim() !== '') {
        checkoutUrl += `?discount=${encodeURIComponent(discountCode.trim())}`;
      }
      
      window.location.href = checkoutUrl;
      
    } catch (error) {
      console.error('Checkout error:', error);
      alert(error.message || '结账时发生错误，请重试');
      this.setLoading(false);
    }
  }
  
  async getProductHandle(productId) {
    try {
      const response = await fetch(`/products.json?ids=${productId}`);
      if (!response.ok) throw new Error('Failed to fetch product');
      const data = await response.json();
      if (data.products && data.products.length > 0) {
        return data.products[0].handle;
      }
      throw new Error('Product not found');
    } catch (error) {
      throw new Error('无法获取产品信息');
    }
  }
  
  async getFirstAvailableVariant(productHandle) {
    try {
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) throw new Error('Failed to fetch product variants');
      const product = await response.json();
      
      // 查找第一个可用的变体
      const availableVariant = product.variants.find(variant => variant.available);
      return availableVariant ? availableVariant.id : null;
    } catch (error) {
      throw new Error('无法获取产品变体信息');
    }
  }
  
  setLoading(loading) {
    if (!this.button || !this.loadingIcon || !this.buttonLabel) return;
    
    if (loading) {
      this.button.disabled = true;
      this.loadingIcon.style.opacity = '1';
      this.buttonLabel.textContent = 'Processing...';
    } else {
      this.button.disabled = false;
      this.loadingIcon.style.opacity = '0';
      this.buttonLabel.textContent = this.originalText;
    }
  }
}

// 滚动画廊管理类
class ScrollingGallery {
  constructor(element) {
    this.element = element;
    this.galleries = element.querySelectorAll('.gallery-images');
    this.isVisible = false;
    this.observer = null;
    
    this.init();
  }
  
  init() {
    // 复制图片以实现无缝循环
    this.galleries.forEach(gallery => {
      const images = gallery.querySelectorAll('.gallery-image');
      if (images.length > 0) {
        images.forEach(img => {
          const clone = img.cloneNode(true);
          gallery.appendChild(clone);
        });
      }
    });
    
    // 设置 Intersection Observer 来优化性能
    this.setupIntersectionObserver();
    
    // 鼠标悬停暂停动画
    this.setupHoverEvents();
  }
  
  setupIntersectionObserver() {
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.startAnimations();
        } else {
          this.pauseAnimations();
        }
      });
    }, {
      threshold: 0.1
    });
    
    this.observer.observe(this.element);
  }
  
  setupHoverEvents() {
    this.element.addEventListener('mouseenter', () => {
      this.pauseAnimations();
    });
    
    this.element.addEventListener('mouseleave', () => {
      if (this.isVisible) {
        this.startAnimations();
      }
    });
  }
  
  startAnimations() {
    this.isVisible = true;
    this.galleries.forEach(gallery => {
      gallery.style.animationPlayState = 'running';
    });
  }
  
  pauseAnimations() {
    this.galleries.forEach(gallery => {
      gallery.style.animationPlayState = 'paused';
    });
  }
  
  destroy() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }
}

// 初始化功能
document.addEventListener('DOMContentLoaded', function() {
  // 注册自定义元素
  if (!customElements.get('product-showcase-checkout')) {
    customElements.define('product-showcase-checkout', ProductShowcaseCheckout);
  }
  
  // 初始化滚动画廊
  const galleries = document.querySelectorAll('.scrolling-gallery');
  galleries.forEach(gallery => {
    new ScrollingGallery(gallery);
  });
});

// Shopify Design Mode 支持
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', function(event) {
    const galleries = event.target.querySelectorAll('.scrolling-gallery');
    galleries.forEach(gallery => {
      new ScrollingGallery(gallery);
    });
  });
  
  document.addEventListener('shopify:section:unload', function(event) {
    // 清理事件监听器
    const galleries = event.target.querySelectorAll('.scrolling-gallery');
    galleries.forEach(gallery => {
      if (gallery.galleryInstance) {
        gallery.galleryInstance.destroy();
      }
    });
  });
} 